/**
 * Hand the main thread back so the browser can run other tasks and paint.
 *
 * `await Promise.resolve()` is NOT a yield — it drains as a microtask inside
 * the current task, which is exactly how a 20k-row import stayed one
 * uninterruptible 4.4 s block despite being written entirely with `await`.
 *
 * `setTimeout(…, 0)` is a real task boundary but Chrome clamps nested timers to
 * ~4 ms, and a chunked restore takes ~100 of them: measured, that alone put
 * ~440 ms back onto the wall clock. A `MessageChannel` message is an unclamped
 * macrotask, so the yields cost effectively nothing while still letting layout,
 * paint and input run between chunks.
 */
export function yieldToTask(): Promise<void> {
  const scheduler = (globalThis as { scheduler?: { yield?: () => Promise<void> } }).scheduler;
  if (typeof scheduler?.yield === "function") return scheduler.yield();
  if (typeof MessageChannel === "undefined") return new Promise((resolve) => setTimeout(resolve, 0));
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
      channel.port1.close();
      resolve();
    };
    channel.port2.postMessage(null);
  });
}
